/**
 * stageProgramViewModel — pure `(reduxState, raw, newChapterIds) → page props` for the bought stage
 * programme of the parent space.
 *
 * Data pattern B (raw payload): `GET /v1/stage-program` answers a single screen, so its payload stays
 * in the Screen (`useStageProgramData`), never in `state.data`. The store only gives the child's first
 * name. The VM owns the day grouping (the revision programme's) and the section titles (« Lundi 26 octobre »).
 */
import { createSelector } from "reselect"

import {
    toUIStageProgramItems,
    toUIStageProgramSummary,
    type ApiStageProgram,
} from "../../../adapters/parent/StageProgram/stageProgramAdapter"
import { formatDayLabel } from "../../../ui/formatters/dateFormatter"
import { type UIStageProgram } from "../../../ui/types"
import { groupRevisionProgramsByDay } from "../RevisionProgram/revisionProgramViewModel"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

// Le store est en JS non typé : on lit le champ qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

/** A stage day reads « Lundi 26 octobre ». */
const stageDayTitle = (iso: string) => formatDayLabel(iso, false)

const selectFirstname = (state: ReduxState): string | null =>
    state?.data?.formData?.user?.firstname ?? selectDisplayedStudent(state)?.firstname ?? null
const selectRaw = (_state: ReduxState, raw: ApiStageProgram | null) => raw
const selectNewChapterIds = (_state: ReduxState, _raw: ApiStageProgram | null, newChapterIds: ReadonlySet<number>) =>
    newChapterIds

export const stageProgramViewModel = createSelector(
    [selectFirstname, selectRaw, selectNewChapterIds],
    (firstname, raw, newChapterIds): UIStageProgram | null =>
        raw
            ? {
                  ...toUIStageProgramSummary(raw, firstname),
                  groups: groupRevisionProgramsByDay(toUIStageProgramItems(raw, newChapterIds), stageDayTitle),
              }
            : null
)
